/**
 * NotificationPrompt — small inline banner shown ONLY for authenticated users
 * whose browser supports Web Push and who haven't yet granted permission.
 *
 * UX rules:
 *  - Never shown before login (gated by useAuth).
 *  - Suppressed for 7 days once dismissed via the "Later" button.
 *  - Permanently hidden once permission is granted or explicitly denied by
 *    the browser (we don't keep nagging — users can re-enable from Profile).
 *  - Permission request fires from the user-gesture "Enable" click, never on
 *    initial render (browsers block silent prompts).
 */
import { useEffect, useState } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth, useLanguage } from "@/App";
import { pushSupported, ensurePushRegistered } from "@/lib/push";

const DISMISS_KEY  = "push_prompt_dismissed_at";
const DISMISS_DAYS = 7;

function dismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

export default function NotificationPrompt() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token || !pushSupported()) return;
    if (Notification.permission !== "default") return;
    if (dismissedRecently()) return;
    setShow(true);
  }, [token]);

  if (!show) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const r = await ensurePushRegistered(token);
      if (r.ok) {
        toast.success(t("تم تفعيل الإشعارات ✅", "Notifications enabled ✅"));
        setShow(false);
      } else if (r.reason === "denied") {
        toast.error(t("تم رفض الإشعارات من المتصفح.", "Browser denied notifications."));
        setShow(false);
      } else {
        toast.error(t("تعذّر تفعيل الإشعارات.", "Could not enable notifications."));
      }
    } catch {
      toast.error(t("حدث خطأ", "An error occurred"));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setShow(false);
  };

  return (
    <div
      className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-yellow-500/30 bg-yellow-500/5 px-4 py-3"
      data-testid="push-prompt"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center shrink-0">
          <Bell className="text-yellow-400" size={16} />
        </div>
        <div className="min-w-0">
          <div className="text-white text-sm font-semibold">
            {t("فعّل الإشعارات لتعرف بكل جديد", "Enable notifications to stay updated")}
          </div>
          <div className="text-[11px] text-slate-400 truncate">
            {t("رسائل الدعم، الردود، تنبيهات السكن والوظائف — كلها مباشرة على جهازك.", "Support replies, messages, listings & job alerts — straight to your device.")}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="ghost" onClick={dismiss} className="text-slate-400 hover:text-white" data-testid="push-prompt-dismiss">
          {t("لاحقاً", "Later")}
        </Button>
        <Button size="sm" onClick={enable} disabled={busy} className="btn-primary" data-testid="push-prompt-enable">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} className="ml-1" />}
          {t("تفعيل", "Enable")}
        </Button>
        <button onClick={dismiss} className="text-slate-500 hover:text-slate-300" aria-label="close" data-testid="push-prompt-close">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
